import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { meetingsApi, type MeetingDetail } from '@/lib/meetings';
import { api } from '@/lib/api';

type Tab = 'summary' | 'transcript' | 'suggestions' | 'recording';
type TranscriptRow = {
  id: number;
  speaker_label: string | null;
  language: string | null;
  text: string;
  start_ms: number;
};
type SuggestionRow = {
  id: number;
  type: string;
  title: string | null;
  body: string;
  ts_ms: number;
  trigger_excerpt: string | null;
};

export function MeetingReview() {
  const { id } = useParams<{ id: string }>();
  const [m, setM] = useState<MeetingDetail | null>(null);
  const [tab, setTab] = useState<Tab>('summary');
  const [transcripts, setTranscripts] = useState<TranscriptRow[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestionRow[]>([]);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const reload = async () => {
    if (!id) return;
    try {
      const meeting = await meetingsApi.get(id);
      setM(meeting);
      const [tx, sg] = await Promise.all([
        api.get<TranscriptRow[]>(`/api/meetings/${id}/transcripts`),
        api.get<SuggestionRow[]>(`/api/meetings/${id}/suggestions`),
      ]);
      setTranscripts(tx);
      setSuggestions(sg);
    } catch (e: any) {
      setErr(e?.message || 'Ошибка');
    }
  };

  useEffect(() => {
    reload();
  }, [id]);

  // Poll while we're waiting for summary
  useEffect(() => {
    if (!m) return;
    if (m.summary_md) return;
    if (m.status !== 'completed') return;
    const t = window.setInterval(reload, 5000);
    return () => window.clearInterval(t);
  }, [m?.summary_md, m?.status]);

  const generateSummary = async () => {
    if (!id) return;
    setGenerating(true);
    try {
      await api.post(`/api/meetings/${id}/summarize`);
      await reload();
    } finally {
      setGenerating(false);
    }
  };

  const downloadExport = () => {
    window.open(`/api/meetings/${id}/export.md`, '_blank');
  };

  const seekTo = (ms: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = ms / 1000;
    setTab('recording');
    void v.play().catch(() => {});
  };

  if (err) return <Center>{err}</Center>;
  if (!m) return <Center>Загружаю…</Center>;

  return (
    <div className="min-h-dvh flex flex-col px-4 sm:px-5 pt-5 pb-20">
      <header className="flex items-center justify-between mb-3">
        <Link to="/" className="btn-ghost gap-1 -ml-2">
          ← Назад
        </Link>
        <h1 className="text-[15px] font-semibold truncate max-w-[60%]">
          {m.title}
        </h1>
        <button
          onClick={downloadExport}
          className="btn-ghost size-10 !p-0"
          aria-label="Экспорт markdown"
          title="Экспорт markdown"
        >
          <ExportIcon />
        </button>
      </header>

      <div className="flex gap-1 overflow-x-auto pb-3 -mx-1 px-1 mb-3">
        {([
          { k: 'summary', label: 'Конспект' },
          { k: 'transcript', label: `Транскрипт (${transcripts.length})` },
          { k: 'suggestions', label: `Подсказки (${suggestions.length})` },
          ...(m.video_path ? [{ k: 'recording' as const, label: 'Запись' }] : []),
        ] as const).map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={clsx(
              'rounded-pill px-3.5 py-1.5 text-sm whitespace-nowrap transition focus-ring',
              tab === t.k
                ? 'bg-accent text-white shadow-glow'
                : 'glass-strong text-ink-1 hover:bg-white/[0.1]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
          className="flex-1"
        >
          {tab === 'summary' && (
            <SummaryTab
              m={m}
              generating={generating}
              onGenerate={generateSummary}
            />
          )}
          {tab === 'transcript' && (
            <TranscriptTab items={transcripts} onJump={seekTo} hasVideo={!!m.video_path} />
          )}
          {tab === 'suggestions' && (
            <SuggestionsTab items={suggestions} onJump={seekTo} hasVideo={!!m.video_path} />
          )}
          {tab === 'recording' && (
            <RecordingTab id={id!} videoRef={videoRef} suggestions={suggestions} onJump={seekTo} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function SummaryTab({
  m,
  generating,
  onGenerate,
}: {
  m: MeetingDetail;
  generating: boolean;
  onGenerate: () => void;
}) {
  if (!m.summary_md) {
    return (
      <Card className="text-center space-y-3 py-10">
        <div className="text-ink-2">
          {m.status === 'completed'
            ? 'Конспект ещё не готов. Запустить вручную?'
            : 'Конспект появится после завершения встречи.'}
        </div>
        {m.status === 'completed' && (
          <Button onClick={onGenerate} disabled={generating}>
            {generating ? 'Готовлю…' : 'Сгенерировать конспект'}
          </Button>
        )}
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <Card>
        <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">
          Контекст
        </div>
        <div className="text-[14px] whitespace-pre-wrap text-ink-2 font-mono leading-relaxed">
          {m.brief_md || '—'}
        </div>
      </Card>
      <Card>
        <Markdown text={m.summary_md} />
      </Card>
    </div>
  );
}

function TranscriptTab({
  items,
  onJump,
  hasVideo,
}: {
  items: TranscriptRow[];
  onJump: (ms: number) => void;
  hasVideo: boolean;
}) {
  if (items.length === 0)
    return (
      <Card className="text-center text-ink-3 py-10">Пусто.</Card>
    );
  return (
    <Card>
      <ul className="space-y-2 font-mono text-[13px]">
        {items.map((t) => (
          <li
            key={t.id}
            className={clsx(
              'rounded-xl px-3 py-2 cursor-pointer transition',
              isUser(t.speaker_label)
                ? 'bg-accent/10 hover:bg-accent/15'
                : 'bg-white/[0.03] hover:bg-white/[0.06]',
            )}
            onClick={() => hasVideo && onJump(t.start_ms)}
          >
            <div className="text-[10px] text-ink-3 mb-0.5 flex items-center gap-2">
              <span>{t.speaker_label ?? 'speaker'}</span>
              {t.language && <span>· {t.language.toUpperCase()}</span>}
              <span className="ml-auto">{formatTs(t.start_ms)}</span>
            </div>
            <div className="text-ink-1">{t.text}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SuggestionsTab({
  items,
  onJump,
  hasVideo,
}: {
  items: SuggestionRow[];
  onJump: (ms: number) => void;
  hasVideo: boolean;
}) {
  if (items.length === 0)
    return <Card className="text-center text-ink-3 py-10">Пусто.</Card>;
  return (
    <ul className="space-y-3">
      {items.map((s) => (
        <li key={s.id}>
          <Card
            className={clsx(
              hasVideo && 'cursor-pointer hover:bg-white/[0.06]',
              'transition',
            )}
            onClick={() => hasVideo && onJump(s.ts_ms)}
          >
            <div className="text-[11px] uppercase tracking-wider text-ink-3 mb-1 flex items-center gap-2">
              <span>{s.type}</span>
              <span className="ml-auto">{formatTs(s.ts_ms)}</span>
            </div>
            {s.title && <div className="font-medium">{s.title}</div>}
            <p className="text-[14px] mt-0.5 whitespace-pre-wrap">{s.body}</p>
            {s.trigger_excerpt && (
              <div className="text-[11px] text-ink-3 italic mt-2">
                ↳ {s.trigger_excerpt}
              </div>
            )}
          </Card>
        </li>
      ))}
    </ul>
  );
}

function RecordingTab({
  id,
  videoRef,
  suggestions,
  onJump,
}: {
  id: string;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  suggestions: SuggestionRow[];
  onJump: (ms: number) => void;
}) {
  return (
    <div className="space-y-3">
      <Card className="!p-2 overflow-hidden">
        <video
          ref={videoRef}
          src={`/api/meetings/${id}/media`}
          controls
          playsInline
          className="w-full rounded-2xl bg-black aspect-video"
        />
      </Card>
      <Card>
        <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">
          Перейти к моменту
        </div>
        <div className="flex flex-wrap gap-1.5">
          {suggestions.length === 0 && (
            <span className="text-ink-3 text-sm">Подсказок не было.</span>
          )}
          {suggestions.map((s) => (
            <button
              key={s.id}
              onClick={() => onJump(s.ts_ms)}
              className="chip hover:bg-white/[0.1]"
            >
              {formatTs(s.ts_ms)} · {s.type}
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Markdown({ text }: { text: string }) {
  // Minimal Markdown render: headings, bullets, bold (for the small subset our
  // backend produces). Avoids pulling a heavy MD library.
  const lines = text.split('\n');
  return (
    <div className="space-y-2 leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith('## ')) {
          return (
            <div key={i} className="text-xs uppercase tracking-wider text-ink-3 pt-3">
              {line.replace('## ', '')}
            </div>
          );
        }
        if (line.startsWith('# ')) {
          return (
            <h2 key={i} className="text-[18px] font-semibold">
              {line.replace('# ', '')}
            </h2>
          );
        }
        if (line.startsWith('- ')) {
          return (
            <div key={i} className="flex gap-2">
              <span className="text-accent">•</span>
              <span dangerouslySetInnerHTML={{ __html: bold(line.slice(2)) }} />
            </div>
          );
        }
        if (line.trim() === '') return <div key={i} className="h-1" />;
        return (
          <p key={i} dangerouslySetInnerHTML={{ __html: bold(line) }} />
        );
      })}
    </div>
  );
}

function bold(s: string) {
  return s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function isUser(s: string | null) {
  return !!s && /^USER/i.test(s);
}

function formatTs(ms: number) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function ExportIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 16V4m0 12l-4-4m4 4l4-4M4 20h16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh grid place-items-center text-ink-2">
      {children}
    </div>
  );
}
